/* eslint-disable no-console */
import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; }>;
}

interface PWAContextData {
  isInstallable: boolean;
  isInstalled: boolean;
  promptInstall: () => Promise<boolean>;
  dismissInstallPrompt: () => void;
  shouldShowInstallModal: boolean;
  setShouldShowInstallModal: (show: boolean) => void;
  neverShowAgain: () => void;
}

const PWAContext = createContext<PWAContextData>({} as PWAContextData);

const STORAGE_KEY = 'pwa_install_prompt_dismissed';

export const PWAProvider = ({ children }: { children: ReactNode; }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [shouldShowInstallModal, setShouldShowInstallModal] = useState(false);

  // Check if app is installed (standalone mode)
  useEffect(() => {
    const checkInstalled = () => {
      // Check if running as standalone PWA
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://');

      setIsInstalled(isStandalone);
    };

    checkInstalled();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkInstalled);

    return () => {
      mediaQuery.removeEventListener('change', checkInstalled);
    };
  }, []);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    // Handler para o evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      // Previne o mini-infobar padrão do navegador
      e.preventDefault();
      // Guarda o evento para ser disparado posteriormente
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Verifica se o usuário já dispensou anteriormente
      const dismissed = localStorage.getItem(STORAGE_KEY);

      if (!dismissed && !isInstalled) {
        // Show modal after a short delay
        setTimeout(() => {
          setShouldShowInstallModal(true);
        }, 2000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShouldShowInstallModal(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isInstalled]);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      console.log('Install prompt not available');
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error prompting install:', error);
      return false;
    }
  }, [deferredPrompt]);

  const dismissInstallPrompt = useCallback(() => {
    setShouldShowInstallModal(false);
  }, []);

  const neverShowAgain = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShouldShowInstallModal(false);
  }, []);

  return (
    <PWAContext.Provider
      value={{
        isInstallable: !!deferredPrompt && !isInstalled,
        isInstalled,
        promptInstall,
        dismissInstallPrompt,
        shouldShowInstallModal,
        setShouldShowInstallModal,
        neverShowAgain,
      }}
    >
      {children}
    </PWAContext.Provider>
  );
};

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export default PWAContext;
